// src/affinity_core_report_builder.jsx
// ─────────────────────────────────────────────────────────────────────────────
// AFFINITY CORE — CROSS-DOMAIN REPORT BUILDER
//
// Organising principle (per the spec): every section of the system is a header,
// and a report is built by picking fields from ONE OR MORE sections. The entity
// is the spine — every domain joins back to it — which is what makes questions
// like "licensed gaming companies whose beneficial owners sit in Australia" or
// "entities where we provide directors" answerable in one pass.
//
// Three steps, left to right: pick fields → add conditions → read results.
// The selected-fields basket is deliberately prominent: chips are coloured by
// their source section, so a cross-domain report is obvious at a glance rather
// than something you have to reason about.
//
// STATUS: this is the builder shell. The field catalogue, join model, filter
// grammar and output are real and complete. Rows currently resolve against the
// preview portfolio below; each domain is tagged with the backing table/RPC it
// reads from once Azure/Entra is in place, and DOMAINS is the single place to
// swap a resolver from preview to live without touching the UI.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useMemo, useEffect } from "react";
import { reportingInternalRefs } from "./affinity_core_rbac";
import { savedReportList, savedReportSave, savedReportTouch, savedReportDelete } from "./affinity_saved_reports_api";
import EntitySearch from "./affinity_entity_search";

const CY   = "#00C4CC";
const NAVY = "#001242";

// ── Section palette. One colour per system section, reused for chips, headers
// and result column groups so a field's origin is never ambiguous.
const SECTIONS = [
  { id:"entity",     label:"Entity Admin",   color:"#274690", bg:"#EAF0FB", source:"entity, entity_profile" },
  { id:"ownership",  label:"Owners & UBOs",  color:"#6B3FA0", bg:"#F1EBFA", source:"entity_ubo, entity_shareholder" },
  { id:"officers",   label:"Directors & Officers", color:"#0077A8", bg:"#E6F7FB", source:"entity_officer" },
  { id:"services",   label:"Services",       color:"#1F6F54", bg:"#E7F4EF", source:"entity_service" },
  { id:"gaming",     label:"Gaming",         color:"#A32D2D", bg:"#FCEBEB", source:"egaming_licence" },
  { id:"compliance", label:"Compliance",     color:"#7B4F1D", bg:"#FDF4DC", source:"creg_entry, risk_review" },
  { id:"documents",  label:"Documents",      color:"#3C3489", bg:"#EEF0FB", source:"document" },
  { id:"billing",    label:"Billing & WIP",  color:"#633806", bg:"#FAEEDA", source:"invoice, wip_entry" },
  { id:"statutory",  label:"Statutory & Filing", color:"#8A5A00", bg:"#FBF0DC", source:"filing_obligation, annual_return" },
  { id:"banking",    label:"Banking & Assets", color:"#00697A", bg:"#E2F4F6", source:"entity_bank, entity_asset" },
  { id:"onboarding", label:"Onboarding",     color:"#4B6A2F", bg:"#EDF4E3", source:"onboarding_case, cdd_item" },
  { id:"crm",        label:"CRM & Fees",     color:"#8C3B6B", bg:"#F9EAF3", source:"crm_lead, fee_schedule" },
  { id:"time",       label:"Time & Recovery",color:"#3F5B8C", bg:"#EAEFF8", source:"timesheet_entry" },
  { id:"people",     label:"Our People",     color:"#5A5A6E", bg:"#F0F0F4", source:"staff, cpd_entry" },
  { id:"meetings",   label:"Meetings & Minutes", color:"#2F6B7A", bg:"#E4F1F4", source:"entity_meeting" },
  { id:"shares",     label:"Shares & Capital",   color:"#6B4C9A", bg:"#EFE9F7", source:"share_class, allotment, transfer" },
  { id:"charges",    label:"Charges & Security", color:"#8A4A2F", bg:"#F8EBE4", source:"entity_charge" },
  { id:"custody",    label:"Assets & Safe Custody", color:"#4A6B2F", bg:"#EDF4E4", source:"entity_asset, safe_custody" },
  { id:"dividends",  label:"Dividends",      color:"#1F5E8C", bg:"#E5EFF7", source:"entity_dividend" },
  { id:"tax",        label:"Tax & Substance", color:"#7A4B00", bg:"#FBF0E0", source:"fatca_crs, substance_test" },
  { id:"procedures", label:"Procedures & Tasks", color:"#5C5C7A", bg:"#EEEEF5", source:"procedure_run, task" },
  { id:"archive",    label:"Archive & Retention", color:"#6E6E6E", bg:"#F2F2F2", source:"archive_box, retention" },
];
const SEC = SECTIONS.reduce((a,s)=>{ a[s.id]=s; return a; },{});

// ── Field catalogue. type drives the condition editor; options drive its values.
const FIELDS = [
  { sec:"entity",     key:"name",        label:"Entity name",          type:"text" },
  { sec:"entity",     key:"ref",         label:"Reference",            type:"text" },
  { sec:"entity",     key:"class",       label:"Internal / client",    type:"enum", options:["Internal","Client"] },
  { sec:"entity",     key:"type",        label:"Entity type",          type:"enum", options:["Company","Trust","Foundation","Partnership"] },
  { sec:"entity",     key:"jur",         label:"Jurisdiction",         type:"enum", options:["Isle of Man","Malta","Cayman Islands","United Kingdom","United States","Cyprus"] },
  { sec:"entity",     key:"status",      label:"Status",               type:"enum", options:["Active","Dormant","In liquidation"] },
  { sec:"entity",     key:"incorporated",label:"Incorporated",         type:"date" },
  { sec:"entity",     key:"yearEnd",     label:"Year end",             type:"text" },

  { sec:"ownership",  key:"uboNames",    label:"Beneficial owners",    type:"list" },
  { sec:"ownership",  key:"uboCountries",label:"UBO country of residence", type:"list", options:["Australia","United Kingdom","Germany","Ireland","France","South Korea","Isle of Man","United States","Nigeria"] },
  { sec:"ownership",  key:"uboPct",      label:"Largest holding %",    type:"number" },
  { sec:"ownership",  key:"uboPep",      label:"Any UBO is a PEP",     type:"bool" },

  { sec:"officers",   key:"directors",   label:"Directors",            type:"list" },
  { sec:"officers",   key:"affinityDirector", label:"We provide directors", type:"bool" },
  { sec:"officers",   key:"directorCount",label:"Number of directors",  type:"number" },

  { sec:"services",   key:"services",    label:"Services provided",    type:"list", options:["Company administration","Trusteeship","Directorship","Registered office","Accounting","Payroll","DPO","Nominee shareholder"] },
  { sec:"services",   key:"admin",       label:"Administrator",        type:"text" },

  { sec:"gaming",     key:"isGaming",    label:"Gaming entity",        type:"bool" },
  { sec:"gaming",     key:"licenceStatus",label:"Licence status",      type:"enum", options:["Licensed","Application in progress","Not licensed","Lapsed"] },
  { sec:"gaming",     key:"regulator",   label:"Gaming regulator",     type:"enum", options:["GSC","MGA","Curaçao","Anjouan","None"] },
  { sec:"gaming",     key:"licenceNo",   label:"Licence number",       type:"text" },

  { sec:"compliance", key:"risk",        label:"Risk rating",          type:"enum", options:["Low","Medium","High","Very High"] },
  { sec:"compliance", key:"reviewDue",   label:"Next review due",      type:"date" },
  { sec:"compliance", key:"cddComplete", label:"CDD complete",         type:"bool" },
  { sec:"compliance", key:"openBreaches",label:"Open breaches",        type:"number" },

  { sec:"documents",  key:"docCount",    label:"Documents on file",    type:"number" },
  { sec:"documents",  key:"missingDocs", label:"Missing required docs",type:"number" },

  { sec:"billing",    key:"feeAnnual",   label:"Annual fee",           type:"number" },
  { sec:"billing",    key:"wip",         label:"Unbilled WIP",         type:"number" },
  { sec:"billing",    key:"agedDebt",    label:"Aged debt",            type:"number" },
  { sec:"billing",    key:"feeCcy",       label:"Fee currency",         type:"enum", options:["GBP","EUR","USD"] },
  { sec:"billing",    key:"lastInvoiced", label:"Last invoiced",        type:"date" },
  { sec:"billing",    key:"onHold",       label:"Billing on hold",      type:"bool" },

  { sec:"statutory",  key:"arDue",        label:"Annual return due",    type:"date" },
  { sec:"statutory",  key:"arFiled",      label:"Annual return filed",  type:"bool" },
  { sec:"statutory",  key:"accountsDue",  label:"Accounts filing due",  type:"date" },
  { sec:"statutory",  key:"esrRequired",  label:"Economic substance applies", type:"bool" },
  { sec:"statutory",  key:"fatcaCrs",     label:"FATCA / CRS status",   type:"enum", options:["Reportable","Non-reportable","Not assessed"] },
  { sec:"statutory",  key:"regime",       label:"Incorporation regime", type:"enum", options:["Companies Act 1931","Companies Act 2006","Foundations Act 2011","Cayman Companies Act","Malta Companies Act"] },
  { sec:"statutory",  key:"regulator",    label:"Regulator",            type:"enum", options:["IOMFSA","GSC","MGA","CIMA","None"] },
  { sec:"statutory",  key:"filingsLate",  label:"Late filings (12m)",   type:"number" },

  { sec:"banking",    key:"banks",        label:"Banks",                type:"list", options:["Barclays","Lloyds","Butterfield","Cayman National","Bank of Valletta","Conister","Capital International"] },
  { sec:"banking",    key:"accountCount", label:"Bank accounts",        type:"number" },
  { sec:"banking",    key:"balanceTotal", label:"Total bank balance",   type:"number" },
  { sec:"banking",    key:"signatories",  label:"Authorised signatories", type:"list" },
  { sec:"banking",    key:"aum",          label:"Assets under management", type:"number" },
  { sec:"banking",    key:"assetTypes",   label:"Asset types",          type:"list", options:["Property","Listed securities","Private equity","Yacht","Aircraft","Artwork","Crypto"] },
  { sec:"banking",    key:"safeCustody",  label:"Items in safe custody", type:"number" },

  { sec:"onboarding", key:"onboardStage", label:"Onboarding stage",     type:"enum", options:["Enquiry","CDD collection","Risk rating","Sign-off","Complete"] },
  { sec:"onboarding", key:"takeOnDate",   label:"Take-on date",         type:"date" },
  { sec:"onboarding", key:"introducer",   label:"Introducer",           type:"text" },
  { sec:"onboarding", key:"sofVerified",  label:"Source of wealth verified", type:"bool" },
  { sec:"onboarding", key:"verifyMethod", label:"Verification method",  type:"enum", options:["Face to face","Certified copy","Electronic verification","Video call","Regulated intermediary"] },
  { sec:"onboarding", key:"cddOutstanding", label:"Outstanding CDD items", type:"number" },

  { sec:"crm",        key:"leadSource",   label:"Lead source",          type:"enum", options:["Referral","Introducer","Direct","Event","Existing client","Website"] },
  { sec:"crm",        key:"sector",       label:"Sector",               type:"enum", options:["Financial services","eGaming","Medicinal Cannabis","Crypto & digital assets","Shipping & aviation","Property","Family office"] },
  { sec:"crm",        key:"office",       label:"Servicing office",     type:"enum", options:["Isle of Man","Malta","Cayman Islands","United Kingdom","Miami","Cyprus"] },
  { sec:"crm",        key:"relManager",   label:"Relationship manager", type:"text" },
  { sec:"crm",        key:"feeReviewDue", label:"Fee review due",       type:"date" },

  { sec:"time",       key:"hoursYtd",     label:"Hours recorded YTD",   type:"number" },
  { sec:"time",       key:"recoveryPct",  label:"Recovery rate %",      type:"number" },
  { sec:"time",       key:"writeOff",     label:"Written off",          type:"number" },
  { sec:"time",       key:"lastActivity", label:"Last time recorded",   type:"date" },

  { sec:"people",     key:"admin2",       label:"Administrator",        type:"text" },
  { sec:"people",     key:"reviewer",     label:"Compliance reviewer",  type:"text" },
  { sec:"people",     key:"directorStaff",label:"Affinity director appointed", type:"list" },
  { sec:"people",     key:"cpdHours",     label:"CPD hours (staff)",    type:"number" },
  { sec:"people",     key:"office",       label:"Servicing office",     type:"enum", options:["Isle of Man","Malta","Cayman Islands","United Kingdom","Miami","Cyprus"] },

  // ── Officers & Directors — the register itself, not just a count
  { sec:"officers",   key:"officerRole",  label:"Officer role",         type:"enum", options:["Director","Alternate director","Secretary","Trustee","Protector","Enforcer","Council member","Nominee","Reporting Officer"] },
  { sec:"officers",   key:"officerNat",   label:"Officer nationality",  type:"list" },
  { sec:"officers",   key:"officerDob",   label:"Officer date of birth",type:"date" },
  { sec:"officers",   key:"appointed",    label:"Date appointed",       type:"date" },
  { sec:"officers",   key:"resigned",     label:"Date resigned",        type:"date" },
  { sec:"officers",   key:"officerTin",   label:"Officer TIN held",     type:"bool" },
  { sec:"officers",   key:"officerRes",   label:"Officer tax residence",type:"list" },
  { sec:"officers",   key:"vacancies",    label:"Board vacancies",      type:"number" },

  // ── Ownership — deeper than a UBO list
  { sec:"ownership",  key:"shareholders", label:"Shareholders",         type:"list" },
  { sec:"ownership",  key:"nomineeHeld",  label:"Nominee shareholding", type:"bool" },
  { sec:"ownership",  key:"uboTin",       label:"UBO TIN held",         type:"bool" },
  { sec:"ownership",  key:"controlNature",label:"Nature of control",    type:"enum", options:["Ownership >25%","Voting rights","Right to appoint directors","Significant influence","Settlor","Beneficiary","Protector"] },
  { sec:"ownership",  key:"uboVerified",  label:"UBO verified",         type:"bool" },

  // ── Meetings & minutes
  { sec:"meetings",   key:"lastMeeting",  label:"Last board meeting",   type:"date" },
  { sec:"meetings",   key:"meetingType",  label:"Meeting type",         type:"enum", options:["Board meeting","AGM","EGM","Face to face","Video call","Telephone"] },
  { sec:"meetings",   key:"meetingsYtd",  label:"Meetings held YTD",    type:"number" },
  { sec:"meetings",   key:"minutesFiled", label:"Minutes filed",        type:"bool" },
  { sec:"meetings",   key:"ubosMet",      label:"UBOs met face to face",type:"bool" },
  { sec:"meetings",   key:"agmDue",       label:"AGM due",              type:"date" },

  // ── Shares & capital
  { sec:"shares",     key:"shareClasses", label:"Share classes",        type:"list", options:["Ordinary","Ordinary A","Ordinary B","Preference","Redeemable preference","Non-voting","Management","Founder"] },
  { sec:"shares",     key:"issuedShares", label:"Shares in issue",      type:"number" },
  { sec:"shares",     key:"authorised",   label:"Authorised capital",   type:"number" },
  { sec:"shares",     key:"paidUp",       label:"Paid up capital",      type:"number" },
  { sec:"shares",     key:"allotmentsYtd",label:"Allotments (12m)",     type:"number" },
  { sec:"shares",     key:"transfersYtd", label:"Transfers (12m)",      type:"number" },
  { sec:"shares",     key:"certsIssued",  label:"Certificates issued",  type:"bool" },

  // ── Charges & security
  { sec:"charges",    key:"chargeCount",  label:"Registered charges",   type:"number" },
  { sec:"charges",    key:"chargeType",   label:"Charge type",          type:"enum", options:["Fixed charge","Floating charge","Debenture","Mortgage","Pledge","Assignment"] },
  { sec:"charges",    key:"chargeHolder", label:"Charge holder",        type:"list" },
  { sec:"charges",    key:"chargeCreated",label:"Charge created",       type:"date" },
  { sec:"charges",    key:"chargeStatus", label:"Charge status",        type:"enum", options:["Outstanding","Partly discharged","Fully discharged","Satisfied"] },

  // ── Assets & safe custody
  { sec:"custody",    key:"custodyItems", label:"Items in safe custody",type:"number" },
  { sec:"custody",    key:"custodyType",  label:"Custody item type",    type:"enum", options:["Share certificate","Title deed","Will","Bearer instrument","Contract","Seal","Other"] },
  { sec:"custody",    key:"custodyLoc",   label:"Custody location",     type:"enum", options:["IOM safe","Malta safe","Cayman safe","UK safe","External vault"] },
  { sec:"custody",    key:"assetValue",   label:"Asset value",          type:"number" },
  { sec:"custody",    key:"insured",      label:"Assets insured",       type:"bool" },
  { sec:"custody",    key:"lastAudited",  label:"Custody last audited", type:"date" },

  // ── Dividends
  { sec:"dividends",  key:"divDeclared",  label:"Dividend declared",    type:"number" },
  { sec:"dividends",  key:"divPaid",      label:"Dividend paid",        type:"number" },
  { sec:"dividends",  key:"divDate",      label:"Dividend date",        type:"date" },
  { sec:"dividends",  key:"divPerShare",  label:"Dividend per share",   type:"number" },
  { sec:"dividends",  key:"interim",      label:"Interim dividend",     type:"bool" },

  // ── Tax & substance
  { sec:"tax",        key:"fatcaStatus",  label:"FATCA classification", type:"enum", options:["Reporting FI","Non-reporting FI","Active NFFE","Passive NFFE","Not assessed"] },
  { sec:"tax",        key:"crsReportable",label:"CRS reportable",       type:"bool" },
  { sec:"tax",        key:"crsJurisdictions", label:"CRS jurisdictions",type:"list" },
  { sec:"tax",        key:"substanceTest", label:"Substance test result",type:"enum", options:["Pass","Fail","Not applicable","Under review"] },
  { sec:"tax",        key:"cigaCount",    label:"Core income generating activities", type:"number" },
  { sec:"tax",        key:"taxResidence", label:"Tax residence",        type:"list" },
  { sec:"tax",        key:"vatRegistered",label:"VAT registered",       type:"bool" },

  // ── Procedures & tasks
  { sec:"procedures", key:"openTasks",    label:"Open tasks",           type:"number" },
  { sec:"procedures", key:"overdueTasks", label:"Overdue tasks",        type:"number" },
  { sec:"procedures", key:"procRunning",  label:"Procedures in progress",type:"list" },
  { sec:"procedures", key:"lastReviewed", label:"Last file review",     type:"date" },

  // ── Archive & retention
  { sec:"archive",    key:"archived",     label:"Archived",             type:"bool" },
  { sec:"archive",    key:"archiveDate",  label:"Date archived",        type:"date" },
  { sec:"archive",    key:"archiveLoc",   label:"Archive location",     type:"text" },
  { sec:"archive",    key:"retentionEnd", label:"Retention expires",    type:"date" },
  { sec:"archive",    key:"destroyDue",   label:"Due for destruction",  type:"bool" },

  // ── Documents (deeper)
  { sec:"documents",  key:"docFolders",   label:"Folders in use",       type:"list" },
  { sec:"documents",  key:"docExpiring",  label:"Documents expiring 90d",type:"number" },
  { sec:"documents",  key:"lastFiled",    label:"Last document filed",  type:"date" },
  { sec:"documents",  key:"unclassified", label:"Unclassified documents",type:"number" },

  // ── Statutory (deeper)
  { sec:"statutory",  key:"formsPending", label:"Statutory forms pending",type:"number" },
  { sec:"statutory",  key:"lastFiling",   label:"Last filing date",     type:"date" },
  { sec:"statutory",  key:"registersGenerated", label:"Registers generated", type:"date" },

  // ── Gaming (deeper)
  { sec:"gaming",     key:"licenceSubtype",label:"Licence subtype",     type:"enum", options:["B2C — Casino","B2C — Sports betting","B2C — Poker","B2B — Platform supply","B2B — Software supply"] },
  { sec:"gaming",     key:"licenceIssued", label:"Licence issued",      type:"date" },
  { sec:"gaming",     key:"licenceExpiry", label:"Licence expiry",      type:"date" },
  { sec:"gaming",     key:"keyOfficials",  label:"Key officials",       type:"list" },
];
const FIELD = FIELDS.reduce((a,f)=>{ a[f.key]=f; return a; },{});
// Fields the preview dataset can answer today. Everything else is catalogued and
// will resolve when the Azure reporting views are connected — shown as "awaiting
// data" rather than silently returning blanks, so nobody mistakes a gap for a nil.
const hasPreview = (key) => ROWS.length > 0 && Object.prototype.hasOwnProperty.call(ROWS[0], key);

// ── Preview portfolio. One row per entity, flattened across domains — the same
// shape the joined view will return, so the resolver swaps without UI changes.
const ROWS = [
  { name:"Meridian Holdings Ltd", ref:"AC-2024-001", class:"Client", type:"Company", jur:"Isle of Man", status:"Active", incorporated:"2018-03-12", yearEnd:"31/03",
    uboNames:["James Harrington"], uboCountries:["United Kingdom"], uboPct:100, uboPep:false,
    directors:["James Harrington","Sarah Cole"], affinityDirector:true, directorCount:2,
    services:["Company administration","Registered office","Directorship"], admin:"Roxy Sheeley",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Medium", reviewDue:"2026-09-30", cddComplete:true, openBreaches:0,
    docCount:48, missingDocs:0, feeAnnual:4200, wip:1850, agedDebt:0 },
  { name:"Harrington Family Trust", ref:"AC-2019-014", class:"Client", type:"Trust", jur:"Isle of Man", status:"Active", incorporated:"2019-07-05", yearEnd:"05/04",
    uboNames:["Emma Harrington","James Harrington"], uboCountries:["United Kingdom"], uboPct:60, uboPep:false,
    directors:["Affinity Trust Ltd"], affinityDirector:true, directorCount:1,
    services:["Trusteeship","Accounting"], admin:"Roxy Sheeley",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"High", reviewDue:"2026-04-14", cddComplete:false, openBreaches:1,
    docCount:63, missingDocs:2, feeAnnual:7500, wip:3200, agedDebt:1250 },
  { name:"Caledonian Ventures Ltd", ref:"AC-2021-032", class:"Client", type:"Company", jur:"Cayman Islands", status:"Active", incorporated:"2021-01-22", yearEnd:"31/12",
    uboNames:["Lena Müller"], uboCountries:["Germany"], uboPct:75, uboPep:false,
    directors:["Lena Müller","Patrick Walsh"], affinityDirector:false, directorCount:2,
    services:["Company administration","Registered office"], admin:"Garry Crossan",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Medium", reviewDue:"2026-11-30", cddComplete:true, openBreaches:0,
    docCount:31, missingDocs:1, feeAnnual:3800, wip:900, agedDebt:0 },
  { name:"Azure Mediterranean Foundation", ref:"AC-2020-008", class:"Client", type:"Foundation", jur:"Malta", status:"Active", incorporated:"2020-09-14", yearEnd:"31/12",
    uboNames:["Marco Vella"], uboCountries:["Malta"], uboPct:100, uboPep:false,
    directors:["Joanne Fenech","Marco Vella"], affinityDirector:true, directorCount:2,
    services:["Company administration","Accounting","Registered office","Directorship"], admin:"Joanne Fenech",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Low", reviewDue:"2027-01-31", cddComplete:true, openBreaches:0,
    docCount:29, missingDocs:0, feeAnnual:5100, wip:640, agedDebt:0 },
  { name:"Thornbury Asset Co Ltd", ref:"AC-2017-055", class:"Client", type:"Company", jur:"United Kingdom", status:"Dormant", incorporated:"2017-06-03", yearEnd:"31/12",
    uboNames:["Peter Thornbury"], uboCountries:["United Kingdom"], uboPct:100, uboPep:false,
    directors:["Peter Thornbury"], affinityDirector:false, directorCount:1,
    services:["Company administration"], admin:"Neil Kelly",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Medium", reviewDue:"2026-12-31", cddComplete:true, openBreaches:0,
    docCount:18, missingDocs:0, feeAnnual:1800, wip:120, agedDebt:0 },
  { name:"Pacific Wealth Trust", ref:"AC-2022-019", class:"Client", type:"Trust", jur:"Cayman Islands", status:"Active", incorporated:"2022-11-18", yearEnd:"31/12",
    uboNames:["Wei Chen"], uboCountries:["Australia"], uboPct:100, uboPep:true,
    directors:["Affinity Trust Ltd"], affinityDirector:true, directorCount:1,
    services:["Trusteeship","Accounting","Directorship"], admin:"Garry Crossan",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"High", reviewDue:"2026-08-31", cddComplete:false, openBreaches:2,
    docCount:52, missingDocs:3, feeAnnual:9200, wip:4100, agedDebt:2400 },
  { name:"Stonebridge Capital Ltd", ref:"AC-2023-041", class:"Client", type:"Company", jur:"Malta", status:"Active", incorporated:"2023-02-07", yearEnd:"31/12",
    uboNames:["Elena Pace"], uboCountries:["Malta"], uboPct:80, uboPep:false,
    directors:["Joanne Fenech"], affinityDirector:true, directorCount:1,
    services:["Company administration","Registered office","Directorship","Accounting"], admin:"Joanne Fenech",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Low", reviewDue:"2027-02-28", cddComplete:true, openBreaches:0,
    docCount:22, missingDocs:0, feeAnnual:4600, wip:1100, agedDebt:0 },
  { name:"North Star Holdings Ltd", ref:"AC-2016-003", class:"Client", type:"Company", jur:"Isle of Man", status:"In liquidation", incorporated:"2016-10-30", yearEnd:"31/12",
    uboNames:["Alan Kneale"], uboCountries:["Isle of Man"], uboPct:100, uboPep:false,
    directors:["Alan Kneale"], affinityDirector:false, directorCount:1,
    services:["Company administration"], admin:"Roxy Sheeley",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"High", reviewDue:"2026-06-30", cddComplete:false, openBreaches:1,
    docCount:41, missingDocs:4, feeAnnual:2200, wip:0, agedDebt:3800 },
  { name:"Apex Growth Fund Ltd", ref:"AC-2023-052", class:"Client", type:"Company", jur:"Cayman Islands", status:"Active", incorporated:"2023-08-12", yearEnd:"31/12",
    uboNames:["Sophie Laurent","David Park"], uboCountries:["France","South Korea"], uboPct:55, uboPep:false,
    directors:["Sophie Laurent","David Park"], affinityDirector:false, directorCount:2,
    services:["Company administration","Nominee shareholder"], admin:"Garry Crossan",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Very High", reviewDue:"2026-08-25", cddComplete:false, openBreaches:3,
    docCount:37, missingDocs:2, feeAnnual:11500, wip:5600, agedDebt:0 },
  { name:"Suncoast Ventures LLC", ref:"AC-2024-007", class:"Client", type:"Company", jur:"United States", status:"Active", incorporated:"2024-03-01", yearEnd:"31/12",
    uboNames:["Ray Delgado"], uboCountries:["United States"], uboPct:100, uboPep:false,
    directors:["Ray Delgado"], affinityDirector:false, directorCount:1,
    services:["Company administration","Registered office"], admin:"Andy Morgan",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Low", reviewDue:"2027-03-01", cddComplete:true, openBreaches:0,
    docCount:12, missingDocs:1, feeAnnual:2400, wip:300, agedDebt:0 },
  { name:"Bluewater Family Trust", ref:"AC-2020-031", class:"Client", type:"Trust", jur:"Cayman Islands", status:"Active", incorporated:"2020-06-19", yearEnd:"31/12",
    uboNames:["Chidi Okafor"], uboCountries:["Nigeria"], uboPct:100, uboPep:true,
    directors:["Affinity Trust Ltd"], affinityDirector:true, directorCount:1,
    services:["Trusteeship","Directorship"], admin:"Garry Crossan",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"High", reviewDue:"2026-10-15", cddComplete:true, openBreaches:0,
    docCount:44, missingDocs:0, feeAnnual:8100, wip:2200, agedDebt:0 },
  { name:"Phoenix eGaming Ltd", ref:"AC-2025-061", class:"Client", type:"Company", jur:"Isle of Man", status:"Active", incorporated:"2025-03-01", yearEnd:"31/12",
    uboNames:["Daniel Reid","Priya Shah"], uboCountries:["Australia","United Kingdom"], uboPct:70, uboPep:false,
    directors:["Roxy Sheeley","Daniel Reid"], affinityDirector:true, directorCount:2,
    services:["Company administration","Registered office","Directorship","DPO"], admin:"Roxy Sheeley",
    isGaming:true, licenceStatus:"Licensed", regulator:"GSC", licenceNo:"GSC-2025-0441",
    risk:"High", reviewDue:"2026-09-01", cddComplete:true, openBreaches:0,
    docCount:57, missingDocs:1, feeAnnual:18500, wip:6400, agedDebt:0 },
  { name:"Meridian Digital Ltd", ref:"AC-2023-058", class:"Client", type:"Company", jur:"Isle of Man", status:"Active", incorporated:"2023-06-01", yearEnd:"31/12",
    uboNames:["James Harrington"], uboCountries:["United Kingdom"], uboPct:100, uboPep:false,
    directors:["Roxy Sheeley","James Harrington"], affinityDirector:true, directorCount:2,
    services:["Company administration","Registered office","Directorship"], admin:"Roxy Sheeley",
    isGaming:true, licenceStatus:"Licensed", regulator:"GSC", licenceNo:"GSC-2023-0218",
    risk:"Medium", reviewDue:"2026-12-01", cddComplete:true, openBreaches:0,
    docCount:33, missingDocs:0, feeAnnual:12400, wip:2900, agedDebt:0 },
  { name:"Southern Cross Interactive Ltd", ref:"AC-2025-070", class:"Client", type:"Company", jur:"Malta", status:"Active", incorporated:"2025-05-20", yearEnd:"31/12",
    uboNames:["Hayley Mercer"], uboCountries:["Australia"], uboPct:100, uboPep:false,
    directors:["Joanne Fenech","Hayley Mercer"], affinityDirector:true, directorCount:2,
    services:["Company administration","Registered office","Directorship","DPO"], admin:"Joanne Fenech",
    isGaming:true, licenceStatus:"Licensed", regulator:"MGA", licenceNo:"MGA/B2C/701/2025",
    risk:"High", reviewDue:"2026-11-20", cddComplete:true, openBreaches:0,
    docCount:26, missingDocs:2, feeAnnual:16200, wip:4800, agedDebt:1500 },
  { name:"Kestrel Gaming Ltd", ref:"AC-2024-044", class:"Client", type:"Company", jur:"Isle of Man", status:"Active", incorporated:"2024-09-09", yearEnd:"31/12",
    uboNames:["Tom Bracken"], uboCountries:["Australia"], uboPct:100, uboPep:false,
    directors:["Tom Bracken"], affinityDirector:false, directorCount:1,
    services:["Company administration","Registered office"], admin:"Roxy Sheeley",
    isGaming:true, licenceStatus:"Application in progress", regulator:"GSC", licenceNo:"—",
    risk:"High", reviewDue:"2026-09-15", cddComplete:false, openBreaches:1,
    docCount:19, missingDocs:5, feeAnnual:9800, wip:7200, agedDebt:0 },
  { name:"Affinity (Isle of Man) Limited", ref:"AFG-IOM", class:"Internal", type:"Company", jur:"Isle of Man", status:"Active", incorporated:"2004-03-01", yearEnd:"31/12",
    uboNames:["Affinity Group Limited"], uboCountries:["Isle of Man"], uboPct:100, uboPep:false,
    directors:["Andy Morgan","Roxy Sheeley"], affinityDirector:true, directorCount:2,
    services:["Company administration"], admin:"Colette Grisdale",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Low", reviewDue:"2027-03-01", cddComplete:true, openBreaches:0,
    docCount:88, missingDocs:0, feeAnnual:0, wip:0, agedDebt:0 },
  { name:"Affinity (Cyprus) Limited", ref:"AFG-CYP", class:"Internal", type:"Company", jur:"Cyprus", status:"Active", incorporated:"2026-07-01", yearEnd:"31/12",
    uboNames:["Affinity Group Limited"], uboCountries:["Isle of Man"], uboPct:100, uboPep:false,
    directors:["Andrew Morgan"], affinityDirector:true, directorCount:1,
    services:["Company administration"], admin:"Andrew Morgan",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Low", reviewDue:"2027-07-01", cddComplete:true, openBreaches:0,
    docCount:14, missingDocs:0, feeAnnual:0, wip:0, agedDebt:0 },
  { name:"Affinity (Malta) Limited", ref:"AFG-MLT", class:"Internal", type:"Company", jur:"Malta", status:"Active", incorporated:"2012-05-14", yearEnd:"31/12",
    uboNames:["Affinity Group Limited"], uboCountries:["Isle of Man"], uboPct:100, uboPep:false,
    directors:["Andy Morgan","Joanne Fenech"], affinityDirector:true, directorCount:2,
    services:["Company administration"], admin:"Joanne Fenech",
    isGaming:false, licenceStatus:"Not licensed", regulator:"None", licenceNo:"—",
    risk:"Low", reviewDue:"2027-03-01", cddComplete:true, openBreaches:0,
    docCount:64, missingDocs:0, feeAnnual:0, wip:0, agedDebt:0 },
];

// ── Condition grammar per field type.
const OPS = {
  text:   [["contains","contains"],["eq","is"],["neq","is not"],["blank","is empty"]],
  enum:   [["eq","is"],["neq","is not"],["in","is any of"]],
  list:   [["has","includes"],["hasnot","does not include"],["count_gte","has at least"]],
  bool:   [["true","is yes"],["false","is no"]],
  number: [["gte","at least"],["lte","at most"],["eq","equals"],["gt","more than"],["lt","less than"]],
  date:   [["before","before"],["after","after"]],
};

function testCond(row, c) {
  const f = FIELD[c.field]; if (!f) return true;
  const v = row[c.field];
  const val = c.value;
  switch (c.op) {
    case "contains": return String(v||"").toLowerCase().includes(String(val||"").toLowerCase());
    case "eq":       return String(v) === String(val);
    case "neq":      return String(v) !== String(val);
    case "blank":    return v == null || v === "" || (Array.isArray(v) && !v.length);
    case "in":       return String(val||"").split(",").map(x=>x.trim().toLowerCase()).filter(Boolean).indexOf(String(v).toLowerCase()) > -1;
    case "has":      return Array.isArray(v) && v.some(x=>String(x).toLowerCase() === String(val||"").toLowerCase());
    case "hasnot":   return Array.isArray(v) && !v.some(x=>String(x).toLowerCase() === String(val||"").toLowerCase());
    case "count_gte":return Array.isArray(v) && v.length >= Number(val||0);
    case "true":     return v === true;
    case "false":    return v === false;
    case "gte":      return Number(v||0) >= Number(val||0);
    case "lte":      return Number(v||0) <= Number(val||0);
    case "gt":       return Number(v||0) >  Number(val||0);
    case "lt":       return Number(v||0) <  Number(val||0);
    case "before":   return String(v||"") <  String(val||"");
    case "after":    return String(v||"") >  String(val||"");
    default:         return true;
  }
}

// ── Starter reports. The first two are the questions from the spec verbatim.
const PRESETS = [
  { id:"gaming_au", name:"Licensed gaming companies with UBOs in Australia",
    note:"The cross-domain case: Gaming licence status joined to UBO country of residence.",
    fields:["name","jur","licenceStatus","regulator","licenceNo","uboNames","uboCountries"],
    conds:[{field:"licenceStatus",op:"eq",value:"Licensed"},{field:"uboCountries",op:"has",value:"Australia"}] },
  { id:"we_direct", name:"Entities where we provide directors",
    note:"Directorship exposure across the portfolio, with risk and jurisdiction for context.",
    fields:["name","jur","type","directors","directorCount","risk","admin"],
    conds:[{field:"affinityDirector",op:"true",value:""}] },
  { id:"cdd_gaps", name:"High-risk entities with incomplete CDD",
    note:"Compliance joined to Entity Admin — the review queue, worst first.",
    fields:["name","jur","risk","cddComplete","reviewDue","openBreaches","admin"],
    conds:[{field:"risk",op:"in",value:"High, Very High"},{field:"cddComplete",op:"false",value:""}] },
  { id:"wip_exposure", name:"Unbilled WIP over £2,000",
    note:"Billing joined to the administrator who owns the relationship.",
    fields:["name","admin","wip","agedDebt","feeAnnual"],
    conds:[{field:"wip",op:"gt",value:"2000"}] },
  { id:"internal", name:"Affinity's own group companies",
    note:"The internal register — our own entities, separated from the client portfolio.",
    fields:["name","ref","jur","type","directors","docCount"],
    conds:[{field:"class",op:"eq",value:"Internal"}] },
];

const fmtCell = (v, f) => {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (f && f.type === "number" && ["feeAnnual","wip","agedDebt"].indexOf(f.key) > -1)
    return "£" + Number(v).toLocaleString();
  if (f && f.type === "date") return String(v).split("-").reverse().join("/");
  return String(v);
};

export default function AffinityReportBuilder({ isAdmin = false, onNav, role = "admin", reportingScopes, userName = "" }) {
  const [picked, setPicked]   = useState(PRESETS[0].fields);
  const [conds, setConds]     = useState(PRESETS[0].conds);
  const [openSecs, setOpenSecs] = useState({ entity:true, gaming:true, ownership:true });
  const [activePreset, setActivePreset] = useState(PRESETS[0].id);
  const [saved, setSaved]     = useState([]);
  const [savedLocal, setSavedLocal] = useState(false);
  const [activeSaved, setActiveSaved] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [name, setName]       = useState(PRESETS[0].name);
  // Client portfolio is open to all staff; Affinity's own companies follow the
  // per-company grants, so a report cannot sidestep segregation.
  const internalOK = reportingInternalRefs(role, reportingScopes);
  const allowed = internalOK.length ? ["client","group"] : ["client"];
  const [scope, setScope]     = useState("all"); // client | internal | all
  const [shareSaved, setShareSaved] = useState(false);
  const [entityFilter, setEntityFilter] = useState("");
  const [tab, setTab]         = useState("build");   // build | saved
  const [savedQ, setSavedQ]   = useState(""); // narrow the whole report to one entity

  const refreshSaved = ()=> savedReportList(userName)
    .then(r=>{ setSaved(r.data||[]); setSavedLocal(!!r.local); })
    .catch(()=>{});
  useEffect(()=>{ refreshSaved(); /* eslint-disable-next-line */ },[]);

  const results = useMemo(()=>{
    let rows = ROWS.filter(r=>allowed.indexOf(r.class === "Internal" ? "group" : "client") > -1);
    if (scope === "client")   rows = rows.filter(r=>r.class === "Client");
    if (scope === "internal") rows = rows.filter(r=>r.class === "Internal");
    if (entityFilter.trim())  rows = rows.filter(r=>r.name === entityFilter.trim());
    return rows.filter(r=>conds.every(c=>testCond(r,c)));
  },[conds, scope, role, reportingScopes, entityFilter]);

  const usedSecs = useMemo(()=>{
    const set = [];
    picked.forEach(k=>{ const f=FIELD[k]; if(f && set.indexOf(f.sec)<0) set.push(f.sec); });
    return set;
  },[picked]);

  const toggleField = (k)=> setPicked(p=> p.indexOf(k)>-1 ? p.filter(x=>x!==k) : p.concat([k]));
  const addCond = ()=> setConds(c=>c.concat([{ field:"name", op:"contains", value:"" }]));
  const setCond = (i, patch)=> setConds(c=>c.map((x,j)=>j===i?{...x,...patch}:x));
  const delCond = (i)=> setConds(c=>c.filter((_,j)=>j!==i));

  const loadPreset = (p)=>{ setPicked(p.fields); setConds(p.conds); setActivePreset(p.id); setName(p.name); };

  // "When I come to run another report it wants to overwrite the first one."
  //
  // Saving is an upsert keyed on the name, and the name field keeps whatever
  // was last loaded — it starts as the first preset's name. So building a
  // second report and pressing Save quietly replaced the first, because the
  // name had not changed and nothing said it was about to.
  //
  // The save now says which of the two it is doing. Overwriting a saved report
  // is a reasonable thing to want; doing it without being told is not.
  const saveReport = async ()=>{
    const nm = name.trim();
    if(!nm){ window.alert("Give the report a name before saving it."); return; }
    if(!picked.length){ window.alert("Pick at least one field before saving."); return; }

    const clash = (saved||[]).find(x => (x.name||"").trim().toLowerCase() === nm.toLowerCase());
    if (clash && clash.id !== activeSaved) {
      const overwrite = window.confirm(
        'A saved report called "' + nm + '" already exists.\n\n' +
        'OK replaces it with what is on screen now.\n' +
        'Cancel leaves it alone so you can give this one a different name.');
      if (!overwrite) return;
    }

    setBusy(true);
    try {
      const r = await savedReportSave(nm, { fields:picked, conds, scope }, userName, shareSaved);
      if(r && r.data) setActiveSaved(r.data.id);
      await refreshSaved();
      window.alert(clash ? 'Replaced "' + nm + '".' : 'Saved as "' + nm + '".');
    } finally { setBusy(false); }
  };

  // Keep the current report and start another from it, rather than editing a
  // saved one into something else and losing the original.
  const saveAsNew = ()=>{
    const nm = window.prompt(
      "Name for this report?\n\nThe one it was built from is left as it is.",
      name.trim() ? name.trim() + " (copy)" : "");
    if(!nm || !nm.trim()) return;
    setName(nm.trim());
    setActiveSaved(null);
  };

  // Re-running a saved report re-evaluates it against current data — it is a
  // definition, not a snapshot, so the answer is always today's.
  const runSaved = async (row)=>{
    const d = row.definition || {};
    setPicked(d.fields||[]);
    setConds(d.conds||[]);
    setScope(d.scope||"all");
    setName(row.name);
    setActiveSaved(row.id);
    setActivePreset(null);
    await savedReportTouch(row.id);
    refreshSaved();
  };

  const removeSaved = async (row)=>{
    await savedReportDelete(row.id);
    if(activeSaved===row.id) setActiveSaved(null);
    refreshSaved();
  };

  const exportCsv = ()=>{
    const head = picked.map(k=>FIELD[k].label).join(",");
    const body = results.map(r=>picked.map(k=>`"${String(fmtCell(r[k],FIELD[k])).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([head+"\n"+body], { type:"text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (name||"affinity-report").toLowerCase().replace(/[^a-z0-9]+/g,"-") + ".csv";
    a.click();
  };

  const btn  = { padding:"6px 12px", fontSize:11, borderRadius:5, border:"0.5px solid #e5e5e5", background:"transparent", color:"#666", cursor:"pointer" };
  const btnP = { ...btn, background:CY, color:"#fff", border:`0.5px solid ${CY}`, fontWeight:600 };
  const th   = { padding:"8px 12px", textAlign:"left", fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px", borderBottom:"0.5px solid #e5e5e5", whiteSpace:"nowrap" };
  const td   = { padding:"8px 12px", fontSize:11, borderBottom:"0.5px solid #f0f0f0", verticalAlign:"top" };
  const inp  = { height:26, padding:"0 7px", fontSize:11, border:"0.5px solid #ccc", borderRadius:4, background:"#fff" };

  return (
    <div style={{ fontFamily:"'Catamaran',system-ui,sans-serif", color:"#111", background:"#f8f9fc", minHeight:"100vh" }}>

      {/* Header */}
      <div style={{ background:"#fff", borderBottom:"0.5px solid #e5e5e5", padding:"14px 22px" }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap" }}>
          <h2 style={{ margin:0, fontSize:19, fontWeight:600, color:NAVY }}>Reporting</h2>
          <div style={{ display:"flex", border:"0.5px solid #e5e5e5", borderRadius:6, overflow:"hidden" }}>
            {[["build","Report builder",null],["saved","Saved reports",saved.length]].map(([v,l,n])=>(
              <button key={v} onClick={()=>setTab(v)}
                style={{ border:"none", cursor:"pointer", fontSize:11.5, padding:"6px 13px", fontWeight:tab===v?600:400,
                         background:tab===v?CY:"#fff", color:tab===v?"#fff":"#666", display:"flex", alignItems:"center", gap:6 }}>
                {l}{n>0 && <span style={{ fontSize:9, fontWeight:700, background:tab===v?"rgba(255,255,255,0.25)":"#eee", color:tab===v?"#fff":"#666", borderRadius:9, padding:"1px 6px" }}>{n}</span>}
              </button>
            ))}
          </div>
          <span style={{ fontSize:11.5, color:"#888" }}>Pick fields from any section of the system. The entity is the spine, so sections combine.</span>
        </div>
        {/* Entity search — same component as Entity Admin. Leave empty to report
            across the whole portfolio; enter an entity to narrow every result. */}
        <div style={{ marginTop:10, maxWidth:460 }}>
          <EntitySearch value={entityFilter} onChange={setEntityFilter} compact
            placeholder="Search for an entity by name or reference… (optional)" />
        </div>
      </div>

      {/* Recently saved — full list lives on the Saved reports tab */}
      {tab==="build" && <div style={{ padding:"12px 22px 0" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7, flexWrap:"wrap" }}>
          <div style={{ fontSize:10, fontWeight:600, color:"#aaa", textTransform:"uppercase", letterSpacing:"0.5px" }}>Saved reports</div>
          <span style={{ fontSize:10.5, color:"#aaa" }}>{saved.length} saved{savedLocal?" · this device only":""}</span>
          {saved.length>0 && <span style={{ fontSize:10.5, color:"#888" }}>— re-runs against current data, never a stale copy</span>}
        </div>
        {saved.length===0 ? (
          <div style={{ fontSize:11.5, color:"#999", padding:"6px 0 2px" }}>
            None yet. Build a report below, name it and save it — it will appear here to run again whenever you need it.
          </div>
        ) : (
          <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
            {saved.map(r=>(
              <div key={r.id} style={{ display:"inline-flex", alignItems:"center", gap:7, border:`0.5px solid ${activeSaved===r.id?CY:"#e5e5e5"}`,
                                       background:activeSaved===r.id?"rgba(0,196,204,0.07)":"#fff", borderRadius:7, padding:"5px 9px" }}>
                <button onClick={()=>runSaved(r)} title={(r.definition&&r.definition.fields?r.definition.fields.length:0)+" columns · "+(r.definition&&r.definition.conds?r.definition.conds.length:0)+" conditions"}
                  style={{ border:"none", background:"none", cursor:"pointer", fontSize:11.5, fontWeight:600,
                           color:activeSaved===r.id?"#00838a":"#333", padding:0, textAlign:"left" }}>
                  {r.name}
                </button>
                {r.shared && <span style={{ fontSize:8.5, fontWeight:700, color:"#1F6F54", background:"#E7F4EF", borderRadius:8, padding:"1px 5px" }}>SHARED</span>}
                {r.run_count>0 && <span style={{ fontSize:9, color:"#aaa" }}>{r.run_count}×</span>}
                <button onClick={()=>removeSaved(r)} title="Delete saved report"
                  style={{ border:"none", background:"none", cursor:"pointer", color:"#bbb", fontSize:11, padding:0, lineHeight:1 }}>✕</button>
              </div>
            ))}
          </div>
        )}
        <details style={{ marginTop:9 }}>
          <summary style={{ fontSize:10.5, color:"#888", cursor:"pointer" }}>Examples to start from</summary>
          <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginTop:7 }}>
            {PRESETS.map(p=>(
              <button key={p.id} onClick={()=>loadPreset(p)} title={p.note}
                style={{ ...btn, background:activePreset===p.id?"#EAF0FB":"#fff", borderColor:activePreset===p.id?"#274690":"#e5e5e5",
                         color:activePreset===p.id?"#274690":"#555", textAlign:"left", maxWidth:270 }}>
                {p.name}
              </button>
            ))}
          </div>
        </details>
      </div>}

      {tab==="build" && <div style={{ display:"flex", gap:14, padding:"14px 22px 70px", alignItems:"flex-start", flexWrap:"wrap" }}>

        {/* ── STEP 1 — fields, grouped by system section ── */}
        <div style={{ width:270, minWidth:250, flexShrink:0, background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:9, overflow:"hidden" }}>
          <div style={{ padding:"10px 13px", borderBottom:"0.5px solid #e5e5e5", fontSize:11, fontWeight:700, color:NAVY }}>
            1 · Fields
            <span style={{ float:"right", fontWeight:400, color:"#aaa", fontSize:10 }}>{picked.length} picked</span>
          </div>
          <div style={{ maxHeight:520, overflowY:"auto" }}>
            {SECTIONS.map(sec=>{
              const fs = FIELDS.filter(f=>f.sec===sec.id);
              const n  = fs.filter(f=>picked.indexOf(f.key)>-1).length;
              const open = !!openSecs[sec.id];
              return (
                <div key={sec.id}>
                  <div onClick={()=>setOpenSecs(o=>({...o,[sec.id]:!o[sec.id]}))}
                    style={{ display:"flex", alignItems:"center", gap:7, padding:"8px 13px", cursor:"pointer",
                             borderBottom:"0.5px solid #f0f0f0", background:open?sec.bg:"#fff" }}>
                    <span style={{ fontSize:9, color:"#aaa", width:9 }}>{open?"▼":"►"}</span>
                    <span style={{ width:3, height:13, background:sec.color, borderRadius:2 }} />
                    <span style={{ fontSize:11.5, fontWeight:600, color:sec.color }}>{sec.label}</span>
                    {n>0 && <span style={{ marginLeft:"auto", fontSize:9, fontWeight:700, color:"#fff", background:sec.color, borderRadius:9, padding:"1px 6px" }}>{n}</span>}
                  </div>
                  {open && (
                    <div style={{ padding:"3px 0 6px" }}>
                      <div style={{ fontSize:9, color:"#bbb", padding:"2px 13px 5px 32px", fontStyle:"italic" }}>{sec.source}</div>
                      {fs.map(f=>(
                        <label key={f.key} style={{ display:"flex", alignItems:"center", gap:7, padding:"4px 13px 4px 32px", cursor:"pointer", fontSize:11.5 }}>
                          <input type="checkbox" checked={picked.indexOf(f.key)>-1} onChange={()=>toggleField(f.key)} style={{ width:13, height:13, cursor:"pointer" }} />
                          <span style={{ color:picked.indexOf(f.key)>-1?"#111":"#666" }}>{f.label}</span>
                          {!hasPreview(f.key) && <span title="Catalogued — resolves when the reporting views are connected"
                            style={{ marginLeft:"auto", fontSize:8.5, color:"#B08A3E", background:"#FDF4DC", borderRadius:8, padding:"1px 5px", whiteSpace:"nowrap" }}>awaiting data</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── STEP 2 + 3 — basket, conditions, results ── */}
        <div style={{ flex:1, minWidth:420, display:"flex", flexDirection:"column", gap:14 }}>

          {/* Basket — the signature element: chips coloured by source section */}
          <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:9, padding:"11px 13px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, fontWeight:700, color:NAVY }}>Columns</span>
              {usedSecs.length>1 && (
                <span style={{ fontSize:10, fontWeight:600, color:"#1F6F54", background:"#E7F4EF", borderRadius:20, padding:"2px 9px" }}>
                  Cross-section · {usedSecs.length} sections joined
                </span>
              )}
              {picked.some(k=>!hasPreview(k)) && (
                <span style={{ fontSize:10, color:"#7B4F1D", background:"#FDF4DC", borderRadius:20, padding:"2px 9px" }}>
                  {picked.filter(k=>!hasPreview(k)).length} of {picked.length} awaiting data
                </span>
              )}
              <button onClick={()=>setPicked([])} style={{ ...btn, marginLeft:"auto", padding:"3px 9px", fontSize:10 }}>Clear</button>
            </div>
            {picked.length===0 ? (
              <div style={{ fontSize:11.5, color:"#999", padding:"8px 0" }}>No columns yet. Tick fields on the left — mix sections freely.</div>
            ) : (
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {picked.map(k=>{
                  const f = FIELD[k]; if(!f) return null;
                  const sec = SEC[f.sec];
                  return (
                    <span key={k} style={{ display:"inline-flex", alignItems:"center", gap:6, background:sec.bg, color:sec.color,
                                           borderRadius:20, padding:"3px 9px", fontSize:11, fontWeight:600 }}>
                      {f.label}
                      <button onClick={()=>toggleField(k)} title="Remove column"
                        style={{ background:"none", border:"none", cursor:"pointer", color:sec.color, fontSize:12, padding:0, lineHeight:1, opacity:0.65 }}>✕</button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Conditions */}
          <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:9, padding:"11px 13px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:9, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, fontWeight:700, color:NAVY }}>2 · Conditions</span>
              <span style={{ fontSize:10, color:"#aaa" }}>all must be true</span>
              <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center" }}>
                <span style={{ fontSize:10, color:"#888" }}>Portfolio</span>
                <select value={scope} onChange={e=>setScope(e.target.value)} style={inp}>
                  {allowed.indexOf("client")>-1 && <option value="client">Managed entities</option>}
                  {allowed.indexOf("group")>-1  && <option value="internal">Affinity internal</option>}
                  {allowed.length>1             && <option value="all">All entities</option>}
                </select>
                <button onClick={addCond} style={btn}>＋ Condition</button>
              </div>
            </div>

            {conds.length===0 && <div style={{ fontSize:11.5, color:"#999" }}>No conditions — every entity in the selected portfolio is returned.</div>}

            {conds.map((c,i)=>{
              const f = FIELD[c.field] || FIELDS[0];
              const ops = OPS[f.type] || OPS.text;
              const sec = SEC[f.sec];
              const needsValue = ["blank","true","false"].indexOf(c.op) < 0;
              return (
                <div key={i} style={{ display:"flex", gap:6, alignItems:"center", marginBottom:6, flexWrap:"wrap" }}>
                  <span style={{ width:3, height:20, background:sec.color, borderRadius:2 }} />
                  <select value={c.field} style={{ ...inp, maxWidth:190 }}
                    onChange={e=>{ const nf=FIELD[e.target.value]; setCond(i,{ field:e.target.value, op:(OPS[nf.type]||OPS.text)[0][0], value:"" }); }}>
                    {SECTIONS.map(s=>(
                      <optgroup key={s.id} label={s.label}>
                        {FIELDS.filter(x=>x.sec===s.id).map(x=><option key={x.key} value={x.key}>{x.label}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <select value={c.op} onChange={e=>setCond(i,{ op:e.target.value })} style={{ ...inp, maxWidth:130 }}>
                    {ops.map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                  {needsValue && (
                    f.options
                      ? <><input list={"rb-opt-"+f.key} value={c.value} onChange={e=>setCond(i,{ value:e.target.value })}
                               placeholder={c.op==="in"?"comma separated":"value"} style={{ ...inp, minWidth:170 }} />
                          <datalist id={"rb-opt-"+f.key}>{f.options.map(o=><option key={o} value={o}/>)}</datalist></>
                      : <input value={c.value} onChange={e=>setCond(i,{ value:e.target.value })}
                          placeholder={f.type==="date"?"YYYY-MM-DD":f.type==="number"?"amount":"value"} style={{ ...inp, minWidth:170 }} />
                  )}
                  <button onClick={()=>delCond(i)} style={{ ...btn, padding:"3px 8px", fontSize:10, borderColor:"#f0c9c9", color:"#A32D2D" }}>Remove</button>
                </div>
              );
            })}
          </div>

          {/* Results */}
          <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:9, overflow:"hidden" }}>
            <div style={{ display:"flex", alignItems:"center", gap:9, padding:"11px 13px", borderBottom:"0.5px solid #e5e5e5", flexWrap:"wrap" }}>
              <span style={{ fontSize:11, fontWeight:700, color:NAVY }}>3 · Results</span>
              <span style={{ fontSize:11, color:"#666" }}>{results.length} {results.length===1?"entity":"entities"}</span>
              {entityFilter.trim() && (
                <span style={{ fontSize:10, fontWeight:600, color:"#00838a", background:"rgba(0,196,204,0.1)", borderRadius:20, padding:"2px 9px" }}>
                  narrowed to {entityFilter.trim()}
                </span>
              )}
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Name this report"
                style={{ ...inp, marginLeft:"auto", minWidth:210, height:28 }} />
              <label style={{ display:"flex", alignItems:"center", gap:4, fontSize:10.5, color:"#666", cursor:"pointer" }}
                title="Shared reports are visible to the whole team">
                <input type="checkbox" checked={shareSaved} onChange={()=>setShareSaved(v=>!v)} style={{ width:13, height:13, cursor:"pointer" }} />
                Share
              </label>
              <button onClick={saveReport} style={btn} disabled={!picked.length||busy||!name.trim()}>
                {busy?"Saving…":saved.some(r=>r.name===name.trim())?"Update saved report":"Save report"}
              </button>
              <button onClick={saveAsNew} style={btn} disabled={busy}
                      title="Keep the saved report as it is and start another from what is on screen">
                Save as new
              </button>
              <button onClick={exportCsv} style={btnP} disabled={!picked.length}>Export CSV</button>
            </div>

            {picked.length===0 ? (
              <div style={{ padding:"44px 20px", textAlign:"center", color:"#999", fontSize:12 }}>Pick at least one column to see results.</div>
            ) : results.length===0 ? (
              <div style={{ padding:"44px 20px", textAlign:"center", color:"#999", fontSize:12 }}>
                Nothing matches these conditions. Loosen one, or switch the portfolio.
              </div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    {/* section band above the column names — shows the join visually */}
                    <tr>
                      {picked.map(k=>{ const sec=SEC[FIELD[k].sec];
                        return <th key={"s"+k} style={{ ...th, padding:"4px 12px", fontSize:9, color:sec.color, background:sec.bg, borderBottom:`2px solid ${sec.color}` }}>{sec.label}</th>; })}
                    </tr>
                    <tr>
                      {picked.map(k=><th key={k} style={{ ...th, color:"#666", background:"#f9f9f9" }}>{FIELD[k].label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r,i)=>(
                      <tr key={i} style={{ background:i%2?"#fcfcfd":"#fff" }}>
                        {picked.map(k=><td key={k} style={td}>
                          {hasPreview(k) ? fmtCell(r[k], FIELD[k])
                            : <span style={{ color:"#c9b17e", fontSize:10 }}>awaiting data</span>}
                        </td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Admin-only */}
          {isAdmin ? (
            <div style={{ background:"#fff", border:`0.5px solid ${CY}`, borderRadius:9, padding:"11px 13px" }}>
              <div style={{ fontSize:11, fontWeight:700, color:NAVY, marginBottom:6 }}>
                Custom SQL <span style={{ fontWeight:400, color:"#888" }}>· system admins only</span>
              </div>
              <div style={{ fontSize:11.5, color:"#666", marginBottom:8, lineHeight:1.6 }}>
                For questions the field picker can't express — recursive ownership chains, period-on-period movement, anything needing a join the builder doesn't model. Runs read-only against the reporting views.
              </div>
              <textarea readOnly value={"-- Example: licensed gaming entities with an Australian UBO\nselect e.name, l.licence_no, u.name as ubo, u.country\nfrom entity e\njoin egaming_licence l on l.entity_id = e.id and l.status = 'Licensed'\njoin entity_ubo u on u.entity_id = e.id and u.country = 'Australia'\nwhere e.entity_class = 'client'\norder by e.name;"}
                style={{ width:"100%", height:118, fontFamily:"ui-monospace,SFMono-Regular,Menlo,monospace", fontSize:11, lineHeight:1.55,
                         border:"0.5px solid #e0e0e0", borderRadius:6, padding:"9px 10px", background:"#fbfbfd", color:"#333",
                         resize:"vertical", boxSizing:"border-box" }} />
              <div style={{ display:"flex", gap:6, marginTop:8, alignItems:"center", flexWrap:"wrap" }}>
                <button style={btn} disabled title="Enabled once the reporting views are live on Azure">Run query</button>
                <span style={{ fontSize:10, color:"#888" }}>Read-only execution arrives with the Azure reporting views — the editor is here so the permission model can be agreed now.</span>
              </div>
            </div>
          ) : (
            <div style={{ background:"#fafbfc", border:"0.5px solid #e5e5e5", borderRadius:9, padding:"10px 13px", fontSize:11, color:"#888" }}>
              Custom SQL reporting is restricted to system admins.
            </div>
          )}

          <div style={{ background:"#FDF4DC", border:"0.5px solid #E5CE9A", borderRadius:8, padding:"10px 13px", fontSize:10.5, color:"#7B4F1D", lineHeight:1.65 }}>
            ⚠️ Builder shell. The field catalogue, join model, conditions, grouping and CSV export are complete and final — rows currently resolve against a preview portfolio of {ROWS.length} entities. Each section header shows the table it will read from. Swapping to live data is one resolver change in <code>DOMAINS</code>, and needs the Azure reporting views plus Entra identity so row-level scoping (internal vs client) is enforced server-side rather than in the browser.
          </div>
        </div>
      </div>}

      {/* ── SAVED REPORTS TAB — the full list, kept off the builder ── */}
      {tab==="saved" && (
        <div style={{ padding:"14px 22px 70px", maxWidth:1100 }}>
          <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:11, flexWrap:"wrap" }}>
            <input value={savedQ} onChange={e=>setSavedQ(e.target.value)} placeholder="Search saved reports…"
              style={{ ...inp, height:32, minWidth:260 }} />
            <span style={{ fontSize:11, color:"#888" }}>{saved.length} saved{savedLocal?" · this device only":""}</span>
            <button style={{ ...btnP, marginLeft:"auto" }} onClick={()=>setTab("build")}>＋ New report</button>
          </div>

          {saved.length===0 ? (
            <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:9, padding:"46px 20px", textAlign:"center", color:"#999", fontSize:12 }}>
              No saved reports yet. Build one on the Report builder tab, name it and save it — for example a report of UK-resident clients saved as “UK client report”, ready to re-run whenever you need it.
            </div>
          ) : (
            <div style={{ background:"#fff", border:"0.5px solid #e5e5e5", borderRadius:9, overflow:"hidden" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Report","Columns","Conditions","Portfolio","Visibility","Runs","Last run",""].map(h=>(
                    <th key={h} style={{ ...th, background:"#f9f9f9", color:"#666" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {saved.filter(r=>!savedQ.trim()||r.name.toLowerCase().includes(savedQ.trim().toLowerCase())).map((r,i)=>{
                    const d = r.definition || {};
                    return (
                      <tr key={r.id} style={{ background:i%2?"#fcfcfd":"#fff" }}>
                        <td style={{ ...td, fontWeight:600 }}>{r.name}</td>
                        <td style={td}>{(d.fields||[]).length}</td>
                        <td style={td}>{(d.conds||[]).length||"—"}</td>
                        <td style={td}>{d.scope==="client"?"Managed":d.scope==="internal"?"Affinity internal":"All entities"}</td>
                        <td style={td}>{r.shared
                          ? <span style={{ fontSize:9.5, fontWeight:700, color:"#1F6F54", background:"#E7F4EF", borderRadius:9, padding:"2px 7px" }}>Team</span>
                          : <span style={{ color:"#999" }}>Private</span>}</td>
                        <td style={td}>{r.run_count||0}</td>
                        <td style={{ ...td, color:"#888" }}>{r.last_run_at ? String(r.last_run_at).slice(0,10).split("-").reverse().join("/") : "never"}</td>
                        <td style={{ ...td, whiteSpace:"nowrap" }}>
                          <button style={{ ...btnP, padding:"3px 10px", fontSize:10 }} onClick={()=>{ runSaved(r); setTab("build"); }}>Run ↗</button>
                          <button style={{ ...btn, padding:"3px 9px", fontSize:10, marginLeft:5, borderColor:"#f0c9c9", color:"#A32D2D" }} onClick={()=>removeSaved(r)}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ fontSize:10.5, color:"#888", marginTop:10, lineHeight:1.6 }}>
            A saved report stores its definition, not a snapshot — running it again evaluates against current data. {savedLocal && "Saved to this browser until db_ops/008_saved_reports.sql is run in the database."}
          </div>
        </div>
      )}
    </div>
  );
}
